import createContentfulClient from "components/content/graphql/apollo/createContentfulClient";
import type {
  IFundamenteiBlogCollectionQuery,
  IFundamenteiBlogCollectionQueryVariables,
} from "components/content/graphql/queries/__generated__/FundamenteiBlogCollectionQuery.generated";
import FundamenteiBlogCollectionQuery from "components/content/graphql/queries/FundamenteiBlogCollectionQuery";
import { IFundamenteiBlogOrder } from "components/content/graphql/types/graphqlTypes";
import NavbarLayout from "components/layouts/NavbarLayout";
import FundamenteiMetadata from "components/seo/FundamenteiMetadata";
import classNames from "components/ui/classNames";
import RelativeTime from "components/ui/RelativeTime";
import getBaseUrl from "lib/utils/getBaseUrl";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import type { GetServerSideProps } from "next";
import { CollectionPageJsonLd } from "next-seo";
import Link from "next/link";
import { FormattedMessage, useIntl } from "react-intl";

interface BlogProps {
  articles: NonNullable<IFundamenteiBlogCollectionQuery["fundamenteiBlogCollection"]>["items"];
  nextPage: boolean;
}
function Blog({ articles = [], nextPage }: BlogProps) {
  const intl = useIntl();
  return (
    <NavbarLayout>
      <FundamenteiMetadata
        title="Blog | Fundamentei"
        description={intl.formatMessage({
          defaultMessage:
            "Acompanhe os nossos últimos conteúdos sobre Buy&Hold, bolsa de valores, investimentos, negócios e economia em geral.",
          id: "DJgqXc",
        })}
        twitter={{
          cardType: "summary_large_image",
          site: "@fundamentei",
        }}
      />

      <CollectionPageJsonLd
        name="Blog | Fundamentei"
        hasPart={articles.filter(notUndefinedOrNull).map((article) => ({
          "@type": "Article",
          name: article.title || "",
          url: `${getBaseUrl()}/blog/${article.slug}`,
        }))}
      />

      <div className="lg:py-6">
        <div className="mx-auto my-8 max-w-screen-xl space-y-1 px-4 xl:px-0">
          <div className="max-w-2xl lg:max-w-4xl">
            <h1 className="text-pretty text-4xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-5xl">
              <FormattedMessage defaultMessage="Blog" id="tv5FG3" />
            </h1>
            <h2 className="mt-2 text-lg/8 text-gray-600 dark:text-gray-400">
              <FormattedMessage
                defaultMessage="Aqui você encontra nossos conteúdos sobre Buy&Hold, análises, investimentos e economia em geral."
                id="5hF7Mu"
              />
            </h2>
          </div>

          <div className="w-full">
            <div
              className={classNames(
                "grid gap-x-6 gap-y-4 border-t border-gray-200 pt-6",
                "dark:border-gray-700",
                "sm:mt-8 sm:pt-8",
                "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4",
              )}
            >
              {articles.filter(notUndefinedOrNull).map((article) => {
                const author = article.authorsCollection?.items[0] || null;

                return (
                  <Link
                    href={`/blog/${article.slug}`}
                    key={article._id}
                    className={classNames(
                      "flex flex-col items-start justify-between gap-y-2 rounded-xl border border-transparent p-3",
                      "hover:border-slate-200 hover:bg-white dark:hover:border-transparent dark:hover:bg-sky-600/10",
                      "sm:max-w-80",
                    )}
                  >
                    <div className="group relative flex h-full flex-col gap-y-2">
                      {article.cover && article.cover.url && (
                        <img
                          alt={article.cover.description || article.cover.title || ""}
                          loading="lazy"
                          src={article.cover.url}
                          className="aspect-video max-h-56 w-full rounded-xl object-cover sm:max-w-80"
                        />
                      )}

                      <RelativeTime
                        timeStamp={article.sys.firstPublishedAt}
                        className="text-sm font-medium text-gray-500 dark:text-gray-400"
                        showFullDate={true}
                      />

                      <h3
                        className={classNames(
                          "text-base font-semibold text-gray-900",
                          "group-hover:text-gray-600",
                          "dark:text-white",
                          "dark:group-hover:text-gray-300",
                        )}
                      >
                        {article.title}
                      </h3>

                      {article.description && false && (
                        <p className="mt-4 line-clamp-3 text-base/6 text-gray-600 dark:text-gray-400">
                          {article.description}
                        </p>
                      )}

                      {author && (
                        <div className="relative mt-auto flex items-center gap-x-2 pt-2">
                          {author?.image && author?.image.url && (
                            <img
                              alt={author.image.title || ""}
                              src={author.image.url}
                              className="size-10 rounded-full bg-gray-50 squircle dark:bg-gray-800"
                            />
                          )}
                          <div>
                            <p className="text-sm font-semibold text-gray-800 dark:text-white">{author.name}</p>
                            {author.role && (
                              <p className="text-sm font-medium text-gray-600 dark:text-gray-300">{author.role}</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
            {articles.length === 0 && (
              <p className="text-center">
                <FormattedMessage defaultMessage="Nenhum artigo encontrado no momento" id="ZQ7iLS" />
              </p>
            )}
          </div>

          {nextPage && (
            <div className="text-center">
              <Link href={`/content?page=${nextPage}`} className="font-medium text-blue-600 hover:underline">
                <FormattedMessage defaultMessage="Ver mais posts" id="df0mkm" />
              </Link>
            </div>
          )}
        </div>
      </div>
    </NavbarLayout>
  );
}

export default Blog;

export const getServerSideProps: GetServerSideProps<any> = async ({ query }) => {
  const client = createContentfulClient();
  const limit = 16;
  const skip = query.page ? (parseInt(query.page as string, 10) - 1) * limit : 0;

  const { data } = await client.query<IFundamenteiBlogCollectionQuery, IFundamenteiBlogCollectionQueryVariables>({
    query: FundamenteiBlogCollectionQuery,
    variables: {
      limit,
      locale: "pt-BR",
      order: [IFundamenteiBlogOrder.SysFirstPublishedAtDesc],
      preview: false,
      skip,
      where: null,
    },
  });

  const totalCount = data?.fundamenteiBlogCollection?.total || 0;
  // const hasNextPage = skip + 1 <= totalCount;
  const nextPage = totalCount > skip + limit ? skip / limit + 2 : null;

  return {
    props: {
      articles: data.fundamenteiBlogCollection?.items || [],
      totalCount,
      nextPage,
    },
  };
};
